/// <reference types="gapi" />

import React, {
  createContext,
  useMemo,
  ReactNode,
  useCallback,
  useState,
  useEffect,
} from 'react';
import useLoadScript from 'src/hooks/useLoadScript';
import { env } from 'src/env';
import useSafeLogger from 'src/hooks/useSafeLogger';

interface LoadedApis {
  drive?: boolean;
  drivePicker?: boolean;
  gapi?: boolean;
  recaptchaEnterprise?: boolean;
}

interface GoogleApiContextProps {
  loadedApis: LoadedApis;
}

const GoogleApiContext = createContext<GoogleApiContextProps>({
  loadedApis: {},
});

interface GoogleApiProviderProps {
  clientId: string;
  nonce?: string;
  children: ReactNode;
}

const GoogleApiProvider = ({ nonce, children }: GoogleApiProviderProps) => {
  const { pgLogError } = useSafeLogger();

  const [loadedApis, setLoadedApis] = useState<LoadedApis>({
    drive: false,
    drivePicker: false,
    gapi: false,
    recaptchaEnterprise: false,
  });

  const [gapiScriptLoaded, setGapiScriptLoaded] = useState<boolean>(false);

  const onPickerLoaded = useCallback(() => {
    setLoadedApis((prevState) => {
      return {
        ...prevState,
        drivePicker: true,
      };
    });
  }, []);
  const onGApiClientLoaded = useCallback(() => {
    const loadClients = async () => {
      try {
        await gapi.client.init({
          apiKey: env.REACT_APP_GOOGLE_API_KEY,
          discoveryDocs: [
            'https://www.googleapis.com/discovery/v1/apis/drive/v3/rest',
          ],
        });
        setLoadedApis((prevState) => {
          return {
            ...prevState,
            gapi: true,
            drive: true,
          };
        });
      } catch {
        console.warn('Unable to init gapi client');
      }
    };
    loadClients();
  }, []);

  useEffect(() => {
    if (gapiScriptLoaded) {
      gapi.load('picker', onPickerLoaded);
      gapi.load('client', onGApiClientLoaded);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gapiScriptLoaded]);

  const onScriptLoadSuccess = useCallback(() => {
    setGapiScriptLoaded(true);
  }, []);

  const onCaptchaScriptLoadSuccess = useCallback(() => {
    setLoadedApis((prevState) => {
      return {
        ...prevState,
        recaptchaEnterprise: true,
      };
    });
  }, []);

  const onScriptLoadError = useCallback(() => {
    pgLogError('Failed to load script');
  }, [pgLogError]);

  const onCaptchaScriptLoadError = useCallback(() => {
    pgLogError('Failed to load Recaptcha script');
  }, [pgLogError]);

  useLoadScript({
    src: 'https://apis.google.com/js/api.js',
    nonce,
    onScriptLoadSuccess,
    onScriptLoadError,
  });

  useLoadScript({
    src:
      'https://www.google.com/recaptcha/enterprise.js?render=' +
      env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY,
    nonce,
    onScriptLoadSuccess: onCaptchaScriptLoadSuccess,
    onScriptLoadError: onCaptchaScriptLoadError,
  });

  const contextValue = useMemo(
    () => ({
      loadedApis,
    }),
    [loadedApis],
  );

  return (
    <GoogleApiContext.Provider value={contextValue}>
      {children}
    </GoogleApiContext.Provider>
  );
};

export { GoogleApiProvider };
export default GoogleApiContext;
